import React, { Fragment } from 'react';

import t from '../../../labels/labelling-utils';
import {
  Color,
  COLOR_SCHEMA_DARK,
  COLOR_SCHEMA_DARK_LIGHT,
  COLOR_SCHEMA_LIGHT,
  ColorSchema,
  ComponentSize,
} from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import { getMatchStatusLabel, isPreMatch } from '../../../utils/match-status';
import { applyColorSchema, getMappedColor, makeClassNames } from '../../../utils/styles';
import { dateFormat } from '../../../utils/times';
import Text, { IText } from '../../atoms/text/text';
import { IMatchRow } from '../match-row/match-row';
import MatchStatusWrapper from '../match-status/match-status-wrapper';
import './match-data.scss';

const DEFAULT_COLOR: Color = 'light';
const COLOR_WITH_SCHEMA_DARK: Color = 'light';
const COLOR_WITH_SCHEMA_LIGHT: Color = 'blue';

const getcolorSchemaMapping = () => {
  return {
    [COLOR_SCHEMA_DARK]: COLOR_WITH_SCHEMA_DARK,
    [COLOR_SCHEMA_LIGHT]: COLOR_WITH_SCHEMA_LIGHT,
    [COLOR_SCHEMA_DARK_LIGHT]: COLOR_WITH_SCHEMA_DARK,
  };
};

export const STANDARD_TYPE = 'standard';
export const DATA_CARD_TYPE = 'data-card';
export type MatchDataVariant = typeof STANDARD_TYPE | typeof DATA_CARD_TYPE;

export interface IMatchData extends IComponent {
  hasVideo?: boolean;
  match: IMatchRow;
  isSlim?: boolean;
  variant?: MatchDataVariant;
}

const classNames = (
  colorSchema: ColorSchema,
  hasVideo: boolean,
  isSlim: boolean,
  isDataCard: boolean,
  size: ComponentSize
) =>
  makeClassNames({
    MatchData: [
      'match-data',
      `${applyColorSchema('match-data', colorSchema)}`,
      isSlim && 'match-data--slim',
      isDataCard && 'match-data--data-card',
      `match-data--card-${size}`,
    ],
    labelCompetition: 'match-data__label-competition',
    labelDate: 'match-data__label-date',
    labelDateLong: 'match-data__label-datelong',
    labelDateShort: 'match-data__label-dateshort',
    labelVenue: ['match-data__label-venue', hasVideo && 'match-data__label-venue--video'],
    status: 'match-data__status',
  });

export const getMatchTitle = (match: IMatchRow): string =>
  match.competitionPhase ? `${match.competitionPhase}` : `${t('match.data.round.title')} ${match.round ?? ''}`;

// Pre-match: competition + date (use dateShort only on Mobile when has Video) + stadio
// Live: competition + stadio + match status label
// Post-match: competition + stadio + label FINE

// FIXME: for flag live and fine, need to be re-organized when UX/PO have the final decision

const getDateProps = (variant: MatchDataVariant): IText => {
  return variant === DATA_CARD_TYPE
    ? {
        fontSize: '16',
        fontWeight: '500',
      }
    : {
        fontSize: '14--16',
        fontWeight: '400',
      };
};

const MatchData = ({
  /* istanbul ignore next: default value */ hasVideo = false,
  /* istanbul ignore next: default value */ isSlim = false,
  /* istanbul ignore next: default value */ variant = STANDARD_TYPE,
  match,
  /* istanbul ignore next: default value on mandatory field */ colorSchema = 'dark',
  /* istanbul ignore next: default value */ size = 'large',
}: IMatchData) => {
  const cn = classNames(colorSchema, hasVideo, isSlim, variant === DATA_CARD_TYPE, size);
  const colorSchemaMapping = getcolorSchemaMapping();
  const color = getMappedColor({ colorSchemaMapping, colorSchema, defaultColor: DEFAULT_COLOR });

  const { customCompetitionName, date, status, venue = '' } = match;
  const label = getMatchStatusLabel(status);
  const dateLong = dateFormat({ date, format: variant === DATA_CARD_TYPE ? 'dddd D MMMM' : 'dddd D MMMM YYYY' });
  const dateShort = dateFormat({ date, format: 'ddd D MMM' });

  // TODO: MOBILE needs "Giornata 12" to be "G 12" but OTTAVI or SEMIFINALE remains UNCHANGED!
  // Should write a FUNCTION or BE will send FE both FULL and SHORT titles?
  // RFC 2021-01-13: no TITLE CHANGE untile BE is ready!
  // const titleShort = title.replace('Giornata', 'G');
  // Update Remainder: also Hero sticky slim have this 'G 12' problem. Wait BE for update.
  const title = variant === STANDARD_TYPE ? getMatchTitle(match) : null;

  return (
    <div className={cn.MatchData}>
      {customCompetitionName && (
        <Text className={cn.labelCompetition} fontWeight="500" fontSize="14--16" color={color}>
          {customCompetitionName}
        </Text>
      )}
      {(variant === STANDARD_TYPE || (variant === DATA_CARD_TYPE && match.status === 'PreMatch')) && (
        <Fragment>
          {dateLong && (
            <Text className={cn.labelDateLong} color={color} {...getDateProps(variant)}>
              {title}
              {title && ' - '}
              {variant === DATA_CARD_TYPE ? dateShort : dateLong}
            </Text>
          )}
          {dateShort && (
            <Text className={cn.labelDateShort} color={color} {...getDateProps(variant)}>
              {title}
              {title && ' - '}
              {dateShort}
            </Text>
          )}
        </Fragment>
      )}
      {/* SHOW VENUE only for PREMATCH and DESKTOP or MOBILE with NO VIDEO */}
      {isPreMatch(status) && (
        <Text className={cn.labelVenue} fontSize="12--14" color={color}>
          {venue}
        </Text>
      )}
      {/* FIXME: spread MATCH to pass ALL interface props (including TEST/DEBUG ones) */}
      {label && <MatchStatusWrapper backgroundColor {...match} className={cn.status} />}
    </div>
  );
};

export default MatchData;
